/**
 * Submissions (M21): one build, a zip of its source and the store listing, sent for review
 * with `mogzo submit`. MOGZO's worker checks it first; a reviewer then plays it (M22).
 */

import type { ReleaseState } from './publishing';
import type { ReviewOutcome } from './review';

const MB = 1024 * 1024;

export const SUBMISSION_STATUSES = [
  /** Made by the CLI; its source zip is on the way. Not shown until it arrives. */
  'uploading',
  'checking',
  'failed_checks',
  'in_review',
  'changes_requested',
  'approved',
  'rejected',
  'withdrawn',
] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

/**
 * A version: a build, its source and the listing (M21). A listing change: only the store
 * listing, once the game is live, with the build staying as it is (M23).
 */
export const SUBMISSION_KINDS = ['version', 'listing'] as const;
export type SubmissionKind = (typeof SUBMISSION_KINDS)[number];

/** A project has at most one submission in these at a time. */
export const ACTIVE_SUBMISSION_STATUSES = ['uploading', 'checking', 'in_review'] as const;

export const SUBMISSION_STATUS_NAMES: Record<SubmissionStatus, string> = {
  uploading: 'Sending',
  checking: 'Checking',
  failed_checks: 'Failed checks',
  in_review: 'In review',
  changes_requested: 'Changes requested',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

export const SUBMISSION_LIMITS = {
  sourceBytes: 100 * MB,
  notes: 1000,
  /** Findings kept per check; the report says how many more there were. */
  findings: 25,
  /** Submissions a developer can start in an hour. */
  perHour: 10,
} as const;

/** The checks, in the order the report shows them. */
export const SUBMISSION_CHECKS = [
  { id: 'manifest', name: 'mogzo.json' },
  { id: 'listing', name: 'Store listing' },
  { id: 'size', name: 'Size' },
  { id: 'virus', name: 'Virus scan' },
  { id: 'code', name: 'Code scan' },
  { id: 'network', name: 'Network' },
  { id: 'packages', name: 'Packages' },
  { id: 'source', name: 'Source zip' },
  { id: 'gameSetup', name: 'Leaderboards and achievements' },
] as const;
export type SubmissionCheckId = (typeof SUBMISSION_CHECKS)[number]['id'];

/**
 * `failed` stops the submission before a reviewer sees it. `warnings` lets it through, with
 * notes for the reviewer (the developer sees them too). `skipped` couldn't run here.
 */
export type CheckOutcome = 'passed' | 'warnings' | 'failed' | 'skipped';

export interface CheckFinding {
  level: 'fail' | 'warn';
  message: string;
  /** Where, in the build or the source zip (`source/…`). */
  file?: string;
  line?: number;
  /** A short piece of the line, for minified code where line numbers don't help. */
  excerpt?: string;
}

export interface CheckResult {
  id: SubmissionCheckId;
  outcome: CheckOutcome;
  /** One line under the check's name: "3.1 MB zipped, 8.4 MB unpacked, 48 files". */
  summary: string;
  findings: CheckFinding[];
  /** Findings past SUBMISSION_LIMITS.findings, left out of `findings`. */
  moreFindings?: number;
}

/** What the game's leaderboards and achievements were when it was sent, as they'll go live. */
export interface SubmittedGameSetup {
  leaderboards: { id: string; name: string }[];
  achievements: { id: string; name: string; points: number }[];
  /** Multiplayer modes (M31); missing from versions sent before them. */
  modes?: { id: string; name: string; kind: 'turns' | 'bases' | 'realtime' }[];
}

/** A submission as the dashboard and the CLI see it. */
export interface SubmissionView {
  id: string;
  kind: SubmissionKind;
  status: SubmissionStatus;
  version: string;
  buildNumber: number;
  notes: string | null;
  /** The device the CLI was signed in on: "Studio laptop". */
  sentFrom: string | null;
  sourceName: string;
  sourceBytes: number;
  sourceDeleted: boolean;
  checks: CheckResult[];
  /** In the review queue: 1 is next. Null unless in review. */
  queuePosition: number | null;
  createdAt: string;
  checksStartedAt: string | null;
  checkedAt: string | null;
  decidedAt: string | null;
  /** The reviewer's decision, once there is one (M22). */
  review: ReviewOutcome | null;
  /** For an approved version: live, replaced, rolled back or skipped; null until then (M23). */
  release: ReleaseState | null;
  /** When it goes live, if the developer asked for it and it hasn’t yet. */
  publishAt: string | null;
  publishedAt: string | null;
}

/** "1st", "2nd", "3rd", "11th". */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const suffixes: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' };
  return `${n}${suffixes[n % 10] ?? 'th'}`;
}

/** The submission's outcome from its checks: any failure stops it. */
export function checksPassed(checks: readonly CheckResult[]): boolean {
  return checks.every((check) => check.outcome !== 'failed');
}

/** The source zip's file name, as reviewers download it: `orbit-hop-1.1.0-source.zip`. */
export function sourceZipName(slug: string, version: string): string {
  return `${slug}-${version}-source.zip`;
}
